/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, faShieldAlt, faClock, faCheckCircle, 
  faArrowLeft, faSpinner, faExclamationTriangle, faRedo
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import '../styles/EmailVerification.css';
import logoImage from '../assets/FINAL LOGO AK.png';

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Get email and user data from navigation state
  const email = location.state?.email || '';
  const firstName = location.state?.firstName || '';
  const lastName = location.state?.lastName || '';
  const signupData = location.state?.signupData || {};

  useEffect(() => {
    // Redirect to signup if no email provided
    if (!email) {
      navigate('/signup');
      return;
    }

    // Timer countdown
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  useEffect(() => {
    // Resend cooldown timer
    if (resendCooldown > 0) {
      const cooldownTimer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(cooldownTimer);
    }
  }, [resendCooldown]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split('');
    while (newOtp.length < 6) newOtp.push('');
    setOtp(newOtp);

    // Focus last filled input
    const lastIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  // Verify OTP
  const handleVerify = async () => {
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify OTP
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/auth/signup-otp/verify-otp`,
        {
          email,
          otp: otpCode
        }
      );

      if (response.data.success) {
        setSuccess(true);
        
        // Wait a moment to show success animation
        setTimeout(() => {
          // Redirect to complete signup with verified email
          navigate('/complete-signup', {
            state: {
              email,
              firstName,
              lastName,
              signupData,
              emailVerified: true
            }
          });
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
      // Reset OTP on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/auth/signup-otp/resend-otp`,
        { email }
      );

      if (response.data.success) {
        setTimeLeft(900); // Reset timer to 15 minutes
        setResendCooldown(60); // 60 seconds cooldown
        setOtp(['', '', '', '', '', '']); // Clear OTP inputs
        inputRefs.current[0]?.focus();
        
        // Show success message briefly
        setError('');
        const successMsg = document.createElement('div');
        successMsg.className = 'resend-success-toast';
        successMsg.textContent = 'New code sent to your email!';
        document.body.appendChild(successMsg);
        setTimeout(() => successMsg.remove(), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="email-verification-container">
      {/* Background Elements */}
      <div className="verification-background">
        <div className="gradient-orb orb-orange"></div>
        <div className="gradient-orb orb-teal"></div>
        <div className="gradient-orb orb-orange-light"></div>
      </div>

      {/* Main Content */}
      <motion.div 
        className="verification-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="verification-header">
          <motion.img 
            src={logoImage} 
            alt="AksharJobs Logo" 
            className="verification-logo"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
          />
          
          <motion.div
            className="verification-icon-wrapper"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 150, delay: 0.3 }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="verification-icon" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Verify Your Email
          </motion.h1>
          
          <motion.p
            className="verification-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            We've sent a 6-digit verification code to
          </motion.p>
          
          <motion.div
            className="email-display"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            <span>{email}</span>
          </motion.div>
        </div>

        {/* OTP Input Section */}
        <AnimatePresence mode="wait">
          {!success ? (
            <motion.div
              className="otp-section"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="otp-inputs">
                {otp.map((digit, index) => (
                  <motion.input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className={`otp-input ${digit ? 'filled' : ''} ${error ? 'error' : ''}`}
                    disabled={loading || success}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + index * 0.05 }}
                  />
                ))}
              </div>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Timer */}
              <motion.div
                className="timer-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <FontAwesomeIcon icon={faClock} className={timeLeft < 60 ? 'urgent' : ''} />
                <span className={timeLeft < 60 ? 'urgent' : ''}>
                  Code expires in {formatTime(timeLeft)}
                </span>
              </motion.div>

              {/* Verify Button */}
              <motion.button
                className="verify-button"
                onClick={handleVerify}
                disabled={loading || otp.join('').length !== 6}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Verifying...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Verify Email
                  </>
                )}
              </motion.button>

              {/* Resend Section */}
              <motion.div
                className="resend-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.0 }}
              >
                <p>Didn't receive the code?</p>
                <button
                  className="resend-button"
                  onClick={handleResend}
                  disabled={loading || resendCooldown > 0}
                >
                  <FontAwesomeIcon icon={faRedo} spin={loading} />
                  {resendCooldown > 0 
                    ? `Resend in ${resendCooldown}s` 
                    : 'Resend Code'}
                </button>
              </motion.div>
            </motion.div>
          ) : (
            // Success Animation
            <motion.div
              className="success-section"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <motion.div
                className="success-icon"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
              </motion.div>
              <h2>Email Verified!</h2>
              <p>Redirecting you to complete your registration...</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Security Notice */}
        <motion.div
          className="security-notice"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
        >
          <FontAwesomeIcon icon={faShieldAlt} />
          <span>Your information is secure and encrypted</span>
        </motion.div>

        {/* Back Button */}
        <motion.button
          className="back-button"
          onClick={() => navigate('/signup')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Signup
        </motion.button>
      </motion.div>
    </div>
  );
};

export default EmailVerification;
